import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@ifixit/ui/lib/utils';
import { type ComponentProps, type ReactNode } from 'react';

const cardVariants = cva(
   'flex items-center gap-3 rounded-2xl border-1 border-border bg-card no-underline',
   {
      variants: {
         variant: {
            product:
               'cursor-pointer text-black dark:text-foreground [@media(hover:hover)]:hover:text-primary [@media(hover:hover)]:hover:border-gray-400 [@media(hover:hover)]:hover:transition-[border,color] [@media(hover:hover)]:hover:duration-150',
         },
      },
   }
);

interface CardProps extends VariantProps<typeof cardVariants> {
   className?: string;
   url?: string;
   children: ReactNode;
}

interface ResourceCardProps extends Omit<CardProps, 'children' | 'variant'> {
   variant?: CardProps['variant'];
   thumbnail?: string;
   title?: ReactNode;
   description?: ReactNode;
   device?: string;
   badges?: ReactNode;
   children?: ReactNode;
}

type CardElementProps = Omit<ComponentProps<'div'>, keyof CardProps | 'ref'>;

/**
 * @deprecated Use the new Card from `@ifixit/ui/components/card` instead.
 */
function Card({ variant, className, children, url, ...cardProps }: CardProps & CardElementProps) {
   const baseClassName = cn(cardVariants({ variant }), 'p-4', className);

   if (url) {
      return (
         <a href={url} className={baseClassName} {...(cardProps as ComponentProps<'a'>)}>
            {children}
         </a>
      );
   }

   return (
      <div className={baseClassName} {...cardProps}>
         {children}
      </div>
   );
}

type ResourceCardElementProps = Omit<ComponentProps<'div'>, keyof ResourceCardProps | 'ref'>;

/**
 * @deprecated Use ResourceCard from `@ifixit/ui/components/resource-card` instead.
 */
function ResourceCard({
   variant = 'product',
   thumbnail,
   title,
   description,
   device,
   badges,
   children,
   ...restProps
}: ResourceCardProps & ResourceCardElementProps) {
   return (
      <Card variant={variant} {...restProps}>
         <div className="size-[56px] rounded-lg border border-gray-200 overflow-hidden">
            <img
               className="size-full object-cover object-center"
               src={thumbnail}
               alt={device ?? (typeof title === 'string' ? title : '')}
            />
         </div>
         <div className="flex flex-col flex-1 gap-1 line-height-normal">
            {title && <div className="font-semibold">{title}</div>}
            {description && <div>{description}</div>}
            {badges && <div className="mt-1.5">{badges}</div>}
            {children}
         </div>
      </Card>
   );
}

export { Card, ResourceCard };
